import { NavLink, Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useStore } from "@/context/StoreContext";
import { useTheme } from "@/context/ThemeContext";
import { roleLabel, slaInfo } from "@/lib/constants";
import { useNow, BrandLogo } from "@/components/ui-z";

const NAV = [
  { to: "/", label: "Dashboard", icon: "fas fa-tachometer-alt", end: true, id: "dashboard" },
  { to: "/chamados", label: "Chamados", icon: "fas fa-ticket-alt", end: true, id: "chamados" },
  { to: "/chamados/novo", label: "Novo Chamado", icon: "fas fa-plus-circle", id: "novo-chamado" },
  { section: "GESTÃO" },
  { to: "/relatorios", label: "Relatórios", icon: "fas fa-chart-pie", id: "relatorios" },
  { to: "/usuarios", label: "Usuários & Níveis", icon: "fas fa-users", id: "usuarios", roles: ["gestor"] },
  { to: "/webhooks", label: "Integrações / APIs", icon: "fas fa-plug", id: "webhooks", roles: ["gestor"] },
  { to: "/customizacao", label: "Customização", icon: "fas fa-cogs", id: "customizacao", roles: ["gestor"] },
];

// Os widgets do AdminLTE (pushmenu, navbar-search, fullscreen) tratam o clique; aqui só evitamos a navegação do link
const noop = (e) => e.preventDefault();

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { tickets, loaded } = useStore();
  const { skin, navbarClass, toggleDarkMode } = useTheme();
  const [q, setQ] = useState("");
  // Sidebar visualmente recolhida (só ícones)? Medido pela largura real, cobrindo recolher/expandir,
  // hover do sidebar-mini e telas pequenas. Define qual dos dois badges de contagem aparece.
  const sidebarRef = useRef(null);
  const [sidebarMini, setSidebarMini] = useState(false);
  useEffect(() => {
    const el = sidebarRef.current;
    if (!el || !window.ResizeObserver) return undefined;
    const ro = new ResizeObserver(([entry]) => setSidebarMini(entry.contentRect.width > 0 && entry.contentRect.width < 120));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const [wrapperClass, setWrapperClass] = useState("");
  const nav = useNavigate();
  const location = useLocation();
  const now = useNow();
  const abertos = tickets.filter((t) => t.status === "aberto").length;
  const risco = tickets.filter((t) => !t.resolved_at && ["warning", "breached"].includes(slaInfo(t, now).state));

  useEffect(() => {
    const cls = ["sidebar-mini", "layout-fixed", skin.navbarFixed && "layout-navbar-fixed", skin.footerFixed && "layout-footer-fixed", skin.textSm && "text-sm", skin.accent].filter(Boolean);
    document.body.classList.add(...cls);
    return () => document.body.classList.remove(...cls);
  }, [skin.navbarFixed, skin.footerFixed, skin.textSm, skin.accent]);

  useEffect(() => {
    document.body.classList.toggle("sidebar-collapse", skin.sidebarCollapsed);
  }, [skin.sidebarCollapsed]);

  // Recalcula a altura do content-wrapper (plugin Layout do AdminLTE) a cada página
  useEffect(() => {
    try { window.jQuery?.("body").Layout("fixLayoutHeight"); } catch { /* AdminLTE não carregado */ }
  }, [location.pathname, wrapperClass]);

  const navFlags = [skin.navFlat && "nav-flat", skin.navLegacy && "nav-legacy", skin.navCompact && "nav-compact", skin.navChildIndent && "nav-child-indent"].filter(Boolean).join(" ");

  return (
    <div className="wrapper">
      {/* Preloader do AdminLTE: cobre a tela até a primeira carga de dados; recolhe (height 0) como no adminlte.js */}
      <div className="preloader flex-column justify-content-center align-items-center" style={loaded ? { height: 0 } : undefined} data-testid="preloader">
        {!loaded && (skin.brandLogo
          ? <img className="animation__shake" src={skin.brandLogo} alt={skin.brandName} height={60} width={60} />
          : <i className="fas fa-3x fa-sync-alt fa-spin text-primary" />)}
      </div>
      {/* Navbar */}
      <nav className={`main-header navbar navbar-expand ${navbarClass}`}>
        {/* Left navbar links */}
        <ul className="navbar-nav">
          <li className="nav-item">
            <a className="nav-link" data-widget="pushmenu" href="#!" role="button" onClick={noop} data-testid="sidebar-toggle"><i className="fas fa-bars" /></a>
          </li>
          <li className="nav-item d-none d-sm-inline-block">
            <Link to="/" className="nav-link">Home</Link>
          </li>
          <li className="nav-item d-none d-sm-inline-block">
            <Link to="/chamados/novo" className="nav-link">Novo chamado</Link>
          </li>
        </ul>

        {/* Right navbar links */}
        <ul className="navbar-nav ml-auto">
          {/* Navbar Search */}
          <li className="nav-item">
            <a className="nav-link" data-widget="navbar-search" href="#!" role="button" onClick={noop}><i className="fas fa-search" /></a>
            <div className="navbar-search-block">
              <form className="form-inline" onSubmit={(e) => { e.preventDefault(); nav(`/chamados?q=${encodeURIComponent(q)}`); }}>
                <div className="input-group input-group-sm">
                  <input data-testid="global-search-input" className="form-control form-control-navbar" type="search" placeholder="Buscar chamado (código, solicitante, palavra-chave)" aria-label="Buscar" value={q} onChange={(e) => setQ(e.target.value)} />
                  <div className="input-group-append">
                    <button className="btn btn-navbar" type="submit"><i className="fas fa-search" /></button>
                    <button className="btn btn-navbar" type="button" data-widget="navbar-search"><i className="fas fa-times" /></button>
                  </div>
                </div>
              </form>
            </div>
          </li>

          {/* Modo noturno / dia */}
          <li className="nav-item">
            <a className="nav-link" href="#!" role="button" data-testid="theme-toggle" onClick={(e) => { e.preventDefault(); toggleDarkMode(); }} title={skin.darkMode ? "Modo dia" : "Modo noturno"}>
              <i className={skin.darkMode ? "fas fa-sun" : "fas fa-moon"} />
            </a>
          </li>

          {/* Notifications Dropdown Menu */}
          <li className="nav-item dropdown">
            <a className="nav-link" data-toggle="dropdown" href="#!" onClick={noop} data-testid="notifications-indicator" title="Chamados com prazo em risco">
              <i className="far fa-bell" />
              {risco.length > 0 && <span data-testid="sla-risk-count" className="badge badge-warning navbar-badge">{risco.length}</span>}
            </a>
            <div className="dropdown-menu dropdown-menu-lg dropdown-menu-right">
              <span className="dropdown-item dropdown-header">{risco.length} chamado(s) com prazo em risco</span>
              {risco.slice(0, 5).map((t) => (
                <div key={t.id}>
                  <div className="dropdown-divider" />
                  <Link to={`/chamados/${t.id}`} className="dropdown-item">
                    <i className={`fas ${slaInfo(t, now).state === "breached" ? "fa-exclamation-triangle text-danger" : "fa-clock text-warning"} mr-2`} />{t.codigo}
                    <span className="float-right text-muted text-sm">{slaInfo(t, now).label}</span>
                  </Link>
                </div>
              ))}
              <div className="dropdown-divider" />
              <Link to="/chamados?sla=risco" className="dropdown-item dropdown-footer">Ver todos</Link>
            </div>
          </li>

          <li className="nav-item">
            <a className="nav-link" data-widget="fullscreen" href="#!" role="button" onClick={noop}><i className="fas fa-expand-arrows-alt" /></a>
          </li>

          {/* User Dropdown Menu */}
          <li className="nav-item dropdown">
            <a className="nav-link" data-toggle="dropdown" href="#!" onClick={noop} data-testid="user-menu" title={roleLabel(user.role)}>
              <i className="fas fa-user-cog" />
            </a>
            <div className="dropdown-menu dropdown-menu-right">
              <div className="dropdown-item-text" data-testid="user-menu-info">
                <b className="d-block">{user.name}</b>
                <small className="text-muted d-block">{user.email}</small>
                <span className="badge badge-secondary">{roleLabel(user.role)}</span>
              </div>
              <div className="dropdown-divider" />
              <a href="#!" data-testid="logout-btn" className="dropdown-item" onClick={(e) => { e.preventDefault(); logout(); nav("/login"); }}>
                <i className="fas fa-sign-out-alt mr-2" />Sair
              </a>
            </div>
          </li>
        </ul>
      </nav>
      {/* /.navbar */}

      {/* Main Sidebar Container */}
      <aside ref={sidebarRef} className={`main-sidebar elevation-4 ${skin.sidebarVariant}`} data-testid="sidebar">
        {/* Brand Logo */}
        <Link to="/" className={`brand-link ${skin.brandVariant} ${skin.brandShowTitle ? "" : "brand-logo-only"}`} title={skin.brandName}>
          <BrandLogo skin={skin} />
          {skin.brandShowTitle && <span className="brand-text font-weight-light" data-testid="brand-name">{skin.brandName}</span>}
        </Link>

        {/* Sidebar */}
        <div className="sidebar">
          {/* Sidebar user panel */}
          <div className="user-panel mt-3 pb-3 mb-3 d-flex">
            <div className="image">
              <span className="img-circle elevation-2 bg-secondary d-inline-block text-center" style={{ width: "2.1rem", height: "2.1rem", lineHeight: "2.1rem" }}>{(user.name || "?")[0].toUpperCase()}</span>
            </div>
            <div className="info">
              <a href="#!" onClick={noop} className="d-block" data-testid="sidebar-user-name">{user.name}</a>
            </div>
          </div>

          {/* Sidebar Menu */}
          <nav className="mt-2">
            <ul className={`nav nav-pills nav-sidebar flex-column ${navFlags}`} data-widget="treeview" role="menu" data-accordion="false">
              {NAV.filter((n) => !n.roles || n.roles.includes(user.role)).map((n, i) =>
                n.section ? (
                  <li key={i} className="nav-header">{n.section}</li>
                ) : (
                  <li key={n.to} className="nav-item">
                    <NavLink to={n.to} end={n.end} data-testid={`nav-${n.id}`} className="nav-link">
                      <i className={`nav-icon ${n.icon}`} />
                      {n.id === "chamados" && abertos > 0 && sidebarMini && <span className="badge badge-danger nav-mini-badge" data-testid="nav-mini-badge">{abertos}</span>}
                      <p>
                        {n.label}
                        {n.id === "chamados" && abertos > 0 && !sidebarMini && <span className="right badge badge-danger">{abertos}</span>}
                      </p>
                    </NavLink>
                  </li>
                )
              )}
            </ul>
          </nav>
          {/* /.sidebar-menu */}
        </div>
        {/* /.sidebar */}
      </aside>

      {/* Content Wrapper. Contains page content */}
      <div className={`content-wrapper ${wrapperClass}`}>
        <Outlet context={{ setWrapperClass }} />
      </div>
      {/* /.content-wrapper */}

      {/* Main Footer */}
      <footer className="main-footer">
        <div className="float-right d-none d-sm-inline">
          <b>Versão</b> 1.0.0
        </div>
        <strong>Copyright &copy; {new Date().getFullYear()} Zitec.</strong> Todos os direitos reservados.
      </footer>
    </div>
  );
}
